import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLearnerAuth } from '../context/LearnerAuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft,
  Mic,
  MicOff,
  Video,
  VideoOff,
  Play,
  Pause,
  Send,
  RotateCcw,
  Clock,
  Target,
  CheckCircle,
  AlertCircle,
  Loader2,
  Volume2,
  BookOpen,
  ChevronRight,
  Award,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const HFS_LOGO = "https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/qxumdsjc_HFS_logo_192x192.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api/learn`;

export default function DailyTaskPage() {
  const { dayNumber } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading, refreshLearner } = useLearnerAuth();
  
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [hasRecording, setHasRecording] = useState(false);
  const isRecordingRef = useRef(false); // ref to avoid stale closure in speech recognition
  
  // Media refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);
  const timerRef = useRef(null);

  // Check auth after loading completes
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/learn-communication');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchTask = async () => {
      if (!token) return;
      
      try {
        const response = await fetch(`${API}/daily-task/${dayNumber}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setTask(data);
          
          // If already submitted, show feedback
          if (data.submission) {
            setFeedback(data.submission.feedback);
            setTranscript(data.submission.transcript || '');
          }
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to load task');
          navigate('/learn-communication/dashboard');
        }
      } catch (error) {
        console.error('Task error:', error);
        toast.error('Failed to load task');
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
    
    return () => {
      // Cleanup
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [token, dayNumber, navigate]);

  const startRecording = async () => {
    try {
      // Request media
      const constraints = task.requires_video 
        ? { audio: true, video: { facingMode: 'user', width: 640, height: 480 } }
        : { audio: true };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (task.requires_video && videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
      // Setup media recorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorder.onstop = () => {
        setHasRecording(true);
      };
      
      mediaRecorder.start();
      
      // Setup speech recognition (Web Speech API - FREE)
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognitionRef.current = recognition;
        
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        
        let finalTranscript = '';
        
        recognition.onresult = (event) => {
          let interimTranscript = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal) {
              finalTranscript += result[0].transcript + ' ';
            } else {
              interimTranscript += result[0].transcript;
            }
          }
          setTranscript(finalTranscript + interimTranscript);
        };
        
        recognition.onerror = (event) => {
          console.error('Speech recognition error:', event.error);
          if (event.error === 'no-speech' || event.error === 'aborted') {
            // Restart recognition if still recording
            if (isRecordingRef.current) {
              try {
                setTimeout(() => {
                  if (isRecordingRef.current) recognition.start();
                }, 300);
              } catch (e) {}
            }
          }
        };
        
        recognition.onend = () => {
          // Restart if still recording (use ref to avoid stale closure)
          if (isRecordingRef.current) {
            try {
              setTimeout(() => {
                if (isRecordingRef.current) recognition.start();
              }, 300);
            } catch (e) {}
          }
        };
        
        recognition.start();
      } else {
        toast.info('Speech recognition not available. Please type your response after recording.');
      }
      
      // Start timer
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      setIsRecording(true);
      isRecordingRef.current = true;
      toast.success('Recording started! Speak clearly.');
      
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone/camera. Please allow permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      setIsRecording(false);
      isRecordingRef.current = false;
      toast.success('Recording stopped!');
    }
  };

  const resetRecording = () => {
    // Stop any ongoing streams or timers
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
    
    // Reset all states
    setTranscript('');
    setHasRecording(false);
    setRecordingTime(0);
    setFeedback(null);
    setIsRecording(false);
    isRecordingRef.current = false;
    audioChunksRef.current = [];
    mediaRecorderRef.current = null;
    
    toast.info('Recording reset. Ready to record again.');
  };

  const submitTask = async () => {
    const trimmedTranscript = transcript.trim();
    
    if (!trimmedTranscript) {
      toast.error('Please record your response or type the transcript');
      return;
    }
    
    // Minimum content check - either from recording or typed
    if (trimmedTranscript.length < 30) {
      toast.error('Please provide a more detailed response (at least 30 characters)');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/submit/${dayNumber}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          day_number: parseInt(dayNumber),
          transcript: trimmedTranscript
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeedback(data.feedback);
        
        if (data.passed) {
          toast.success('Great job! You passed this task! 🎉');
          refreshLearner();
          
          if (data.journey_complete) {
            toast.success('Congratulations! You completed the 30-day journey! 🏆');
          }
        } else {
          toast.info('Keep practicing! You can retry this task.');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Submission failed');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-hfs-navy mx-auto mb-4" />
          <p className="text-slate-600">Loading task...</p>
        </div>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/learn-communication/dashboard')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <span className="text-slate-300">|</span>
              <span className="font-medium text-hfs-navy">Day {dayNumber}</span>
            </div>
            <img src={HFS_LOGO} alt="HFS" className="h-8 w-8" />
          </div>
        </div>
      </nav>

      <main className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Task Header */}
          <div className="bg-gradient-to-r from-hfs-navy to-blue-800 rounded-2xl p-6 text-white mb-6">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-2">
              <span>{task.phase}</span>
              <span>•</span>
              <span>Day {task.day_number}/30</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{task.title}</h1>
            <p className="text-white/80">Skill Focus: {task.skill_focus}</p>
            
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full">
                <Clock className="h-4 w-4" />
                <span>{task.time_required} mins</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full">
                <Target className="h-4 w-4" />
                <span>Pass: {task.pass_threshold}/10</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full">
                {task.requires_video ? <Video className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                <span>{task.requires_video ? 'Video' : 'Audio'} Task</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full">
                <RotateCcw className="h-4 w-4" />
                <span>Attempts: {task.retries_used} (Unlimited)</span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column - Instructions */}
            <div className="space-y-6">
              {/* Task Instructions */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-hfs-navy mb-4 flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Your Task
                </h2>
                <p className="text-slate-700 leading-relaxed">{task.instruction}</p>
              </div>

              {/* Model Script */}
              <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
                <h2 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <Volume2 className="h-5 w-5" />
                  Model Script (Reference)
                </h2>
                <p className="text-blue-800 leading-relaxed italic">"{task.model_script}"</p>
                <p className="text-blue-600 text-sm mt-3">
                  💡 Use this as a reference, but speak in your own words!
                </p>
              </div>
            </div>

            {/* Right Column - Recording */}
            <div className="space-y-6">
              {/* Recording Area */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-hfs-navy mb-4 flex items-center gap-2">
                  {task.requires_video ? <Video className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                  Record Your Response
                </h2>

                {/* Video Preview */}
                {task.requires_video && (
                  <div className="mb-4 rounded-lg overflow-hidden bg-slate-900 aspect-video">
                    <video 
                      ref={videoRef} 
                      autoPlay 
                      muted 
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Recording Timer */}
                <div className="text-center mb-4">
                  <div className={`text-4xl font-mono font-bold ${isRecording ? 'text-red-500' : 'text-slate-700'}`}>
                    {formatTime(recordingTime)}
                  </div>
                  <p className="text-sm text-slate-500">
                    Min: {Math.floor(task.min_speaking_time / 60)}:{(task.min_speaking_time % 60).toString().padStart(2, '0')}
                  </p>
                </div>

                {/* Recording Controls */}
                <div className="flex items-center justify-center gap-4">
                  {!isRecording ? (
                    <Button
                      onClick={startRecording}
                      className="bg-red-500 hover:bg-red-600 text-white h-14 px-8"
                      disabled={feedback?.passed || submitting}
                      data-testid="start-recording-btn"
                    >
                      {task.requires_video ? <Video className="mr-2 h-5 w-5" /> : <Mic className="mr-2 h-5 w-5" />}
                      Start Recording
                    </Button>
                  ) : (
                    <Button
                      onClick={stopRecording}
                      className="bg-slate-800 hover:bg-slate-900 text-white h-14 px-8"
                      data-testid="stop-recording-btn"
                    >
                      {task.requires_video ? <VideoOff className="mr-2 h-5 w-5" /> : <MicOff className="mr-2 h-5 w-5" />}
                      Stop Recording
                    </Button>
                  )}
                  
                  {hasRecording && !isRecording && (
                    <Button
                      variant="outline"
                      onClick={resetRecording}
                      disabled={submitting}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset
                    </Button>
                  )}
                </div>

                {/* Transcript */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Transcript (auto-generated or type manually)
                  </label>
                  <textarea
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    placeholder="Your speech will appear here as you speak, or you can type it manually..."
                    className="w-full h-32 p-3 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-hfs-navy focus:border-transparent"
                    disabled={isRecording}
                    data-testid="transcript-input"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  onClick={submitTask}
                  disabled={!transcript.trim() || isRecording || submitting || feedback?.passed}
                  className="w-full mt-4 bg-hfs-navy hover:bg-hfs-navy-dark h-12"
                  data-testid="submit-task-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing with AI...
                    </>
                  ) : feedback?.passed ? (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Task Completed!
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Submit for AI Feedback
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* AI Feedback Panel */}
          {feedback && (
            <div className={`mt-6 rounded-xl border-2 p-6 ${
              feedback.passed 
                ? 'bg-green-50 border-green-200' 
                : 'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-center gap-3 mb-6">
                {feedback.passed ? (
                  <CheckCircle className="h-8 w-8 text-green-500" />
                ) : (
                  <AlertCircle className="h-8 w-8 text-orange-500" />
                )}
                <div>
                  <h2 className="text-xl font-bold text-slate-800">
                    {feedback.passed ? 'Great Job! Task Passed! 🎉' : 'Keep Practicing!'}
                  </h2>
                  <p className="text-slate-600">
                    {feedback.passed 
                      ? 'You met the threshold. Move to the next day!' 
                      : `You need ${task.pass_threshold}/10 to pass. You can retry unlimited times!`}
                  </p>
                </div>
              </div>

              {/* Scores */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Grammar', score: feedback.grammar_score, color: 'blue' },
                  { label: 'Pronunciation', score: feedback.pronunciation_score, color: 'purple' },
                  { label: 'Fluency', score: feedback.fluency_score, color: 'green' },
                  { label: 'Confidence', score: feedback.confidence_score, color: 'orange' },
                ].map((item) => (
                  <div key={item.label} className="bg-white rounded-lg p-4 text-center">
                    <div className={`text-2xl font-bold text-${item.color}-600`}>
                      {(item.score ?? 0).toFixed(1)}/10
                    </div>
                    <div className="text-sm text-slate-500">{item.label}</div>
                    <Progress 
                      value={item.score * 10} 
                      className="h-2 mt-2"
                    />
                  </div>
                ))}
              </div>

              {/* Overall Score */}
              <div className="bg-white rounded-lg p-4 mb-6 text-center">
                <div className="text-3xl font-bold text-hfs-navy">
                  {(feedback.overall_score ?? 0).toFixed(1)}/10
                </div>
                <div className="text-slate-500">Overall Score</div>
              </div>

              {/* What You Did Well */}
              <div className="bg-white rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  What You Did Well
                </h3>
                <ul className="space-y-2">
                  {feedback.what_you_did_well?.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700">
                      <span className="text-green-500 mt-1">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* What Needs Improvement */}
              <div className="bg-white rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-orange-700 mb-3 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  What Needs Improvement
                </h3>
                <ul className="space-y-2">
                  {feedback.what_needs_improvement?.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-700">
                      <span className="text-orange-500 mt-1">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Actionable Fix */}
              <div className="bg-white rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  Actionable Fix
                </h3>
                <p className="text-slate-700">{feedback.actionable_fix}</p>
              </div>

              {/* YouTube Recommendations */}
              {feedback.youtube_recommendations?.length > 0 && (
                <div className="bg-white rounded-lg p-4">
                  <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <ExternalLink className="h-5 w-5" />
                    Recommended Videos
                  </h3>
                  <ul className="space-y-2">
                    {feedback.youtube_recommendations.map((video, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-700">
                        <span className="text-red-500 mt-1">▶</span>
                        <span>{video.title} - {video.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Next Steps */}
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                {feedback.passed ? (
                  <>
                    <Button
                      onClick={() => navigate('/learn-communication/dashboard')}
                      className="bg-hfs-navy hover:bg-hfs-navy-dark"
                    >
                      Go to Dashboard
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                    <Button
                      onClick={resetRecording}
                      variant="outline"
                      className="border-hfs-navy text-hfs-navy hover:bg-hfs-navy/5"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Practice Again
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={resetRecording}
                    className="bg-orange-500 hover:bg-orange-600"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Try Again (Unlimited)
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
